import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { upright, uprightStrip } from './uprightStrip';

/**
 * A straight flight of masonry steps from a quay or bridge head down into the water, in its frame:
 * x runs from the face the flight leaves (x = 0) down the flight, y is world height and z runs
 * across, centred on the flight. Every value comes from outside; for the Nieuwe Brug's west end
 * they are readings of the painting (docs/plans/streetscape.md section 6.4).
 */
export interface WaterStepsSpec {
  readonly width: number;
  readonly treads: number;
  readonly rise: number;
  /** The depth of each tread. */
  readonly going: number;
  /** The level the flight leaves from; the first tread is one rise below it. */
  readonly topY: number;
  /** The foot of the masonry, below the water. */
  readonly footY: number;
}

/** The height of tread `k`, counted from 1 at the top of the flight. */
export function treadY(spec: WaterStepsSpec, k: number): number {
  return spec.topY - k * spec.rise;
}

function fail(message: string): never {
  throw new RangeError(`Water steps: ${message}`);
}

function checkSpec(spec: WaterStepsSpec): void {
  const sizes = [spec.width, spec.rise, spec.going];
  if (!sizes.every((size) => Number.isFinite(size) && size > 0)) {
    fail('width, rise and going must be positive');
  }
  if (!(Number.isInteger(spec.treads) && spec.treads >= 1)) {
    fail('the tread count must be a whole number of at least one');
  }
  if (!(Number.isFinite(spec.topY) && Number.isFinite(spec.footY))) {
    fail('the top and the foot must be heights');
  }
  if (!(treadY(spec, spec.treads) > spec.footY)) {
    fail('the lowest tread must stand above the foot');
  }
}

function treads(spec: WaterStepsSpec): number[] {
  return Array.from({ length: spec.treads }, (_, i) => i + 1);
}

/** The outline of a side face, counter-clockwise: the foot, the front, then up the flight to the top. */
export function stepsProfile(spec: WaterStepsSpec): Vec2[] {
  checkSpec(spec);
  const run = spec.treads * spec.going;
  const steps = treads(spec)
    .reverse()
    .flatMap((k): Vec2[] => [
      [k * spec.going, treadY(spec, k)],
      [(k - 1) * spec.going, treadY(spec, k)],
    ]);
  return [[0, spec.footY], [run, spec.footY], ...steps];
}

// Under tread k, from its heel to its nosing. The nosing edge carries the next tread's heel, so the
// slab shares every corner with the riser below it and with the next slab.
function slab(spec: WaterStepsSpec, k: number): ReturnType<typeof uprightStrip> {
  const below = k < spec.treads ? [treadY(spec, k + 1)] : [];
  return uprightStrip(
    upright((k - 1) * spec.going, [spec.footY, treadY(spec, k)]),
    upright(k * spec.going, [spec.footY, ...below, treadY(spec, k)])
  );
}

type Quad = readonly [Vec3, Vec3, Vec3, Vec3];

// A level face from x0 to x1 at height y, facing up when `up`, down otherwise.
function level(x0: number, x1: number, y: number, half: number, up: boolean): Quad {
  const [z0, z1] = up ? [half, -half] : [-half, half];
  return [
    [x0, y, z0],
    [x1, y, z0],
    [x1, y, z1],
    [x0, y, z1],
  ];
}

// An upright face across the flight at x from y0 to y1, facing down the flight when `ahead`.
function across(x: number, y0: number, y1: number, half: number, ahead: boolean): Quad {
  const [z0, z1] = ahead ? [half, -half] : [-half, half];
  return [
    [x, y0, z0],
    [x, y0, z1],
    [x, y1, z1],
    [x, y1, z0],
  ];
}

function quadsOf(spec: WaterStepsSpec): Quad[] {
  const half = spec.width / 2;
  const g = spec.going;
  const perTread = treads(spec).flatMap((k): Quad[] => [
    level((k - 1) * g, k * g, treadY(spec, k), half, true),
    level((k - 1) * g, k * g, spec.footY, half, false),
    across(k * g, k < spec.treads ? treadY(spec, k + 1) : spec.footY, treadY(spec, k), half, true),
  ]);
  return [...perTread, across(0, spec.footY, treadY(spec, 1), half, false)];
}

// Level faces carry x and z as their pattern coordinates, upright faces z and y.
function uvOf(quad: Quad): [Vec2, Vec2, Vec2, Vec2] {
  const isLevel = quad.every((p) => p[1] === quad[0][1]);
  return quad.map((p): Vec2 => (isLevel ? [p[0], p[2]] : [p[2], p[1]])) as [Vec2, Vec2, Vec2, Vec2];
}

/**
 * The flight as one closed solid facing outward: a tread and a riser for every step, the front down
 * to the foot, the back against the face it leaves, the underside and both side faces. Throws a
 * RangeError for sizes that are not positive or a flight that would reach below its foot.
 */
export function buildWaterSteps(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: WaterStepsSpec
): void {
  checkSpec(spec);
  const half = spec.width / 2;
  const at = (p: Vec2, z: number): Vec3 => [p[0], p[1], z];
  const sideUv = (p: Vec3): Vec2 => [p[0], p[1]];
  for (const k of treads(spec)) {
    for (const [p, q, r] of slab(spec, k)) {
      builder.polygon(style, frame, [at(p, half), at(q, half), at(r, half)], sideUv);
      builder.polygon(style, frame, [at(r, -half), at(q, -half), at(p, -half)], sideUv);
    }
  }
  for (const quad of quadsOf(spec)) builder.quad(style, frame, quad, uvOf(quad));
}
